/**
 * Composition-root helper that builds the platform runtime from the browser
 * Entra configuration.
 *
 * Microsoft Entra is the sole identity provider, so the app always talks to the
 * real API over GraphQL, presenting MSAL-acquired bearer tokens. Keeping the
 * wiring here, separate from the MSAL bootstrap and the React tree, makes it
 * unit-testable without a browser or a live tenant.
 *
 * @module platform/runtime-selection
 */

import type { EntraBrowserConfig } from "@/auth/entra-browser";

import { createGraphqlProgrammePlatformRuntime, type ProgrammePlatformRuntime } from "./runtime";

/** Which persistence path the app is wired to. Entra is the only one. */
export type PlatformRuntimeMode = "entra";

/** The chosen runtime and the mode it represents. */
export interface PlatformRuntimeSelection {
  runtime: ProgrammePlatformRuntime;
  mode: PlatformRuntimeMode;
}

/** Inputs to {@link selectPlatformRuntime}; the runtime factory is injectable for tests. */
export interface SelectPlatformRuntimeOptions {
  /** Resolved Entra config; the composition root only builds a runtime once it has one. */
  config: EntraBrowserConfig;
  /** Bearer-token source for the runtime. */
  getAccessToken: () => Promise<string>;
  /** Override the runtime factory (tests). */
  createRuntime?: typeof createGraphqlProgrammePlatformRuntime;
}

/**
 * Build the platform runtime for the resolved Entra configuration.
 *
 * Returns a GraphQL runtime pointed at the configured API base URL and driven
 * by `getAccessToken`.
 *
 * @param options - The Entra config, token source, and optional factory override.
 * @returns The chosen runtime and its mode.
 */
export function selectPlatformRuntime(
  options: SelectPlatformRuntimeOptions,
): PlatformRuntimeSelection {
  const createRuntime = options.createRuntime ?? createGraphqlProgrammePlatformRuntime;

  return {
    runtime: createRuntime({
      baseUrl: options.config.apiBaseUrl,
      getAccessToken: options.getAccessToken,
    }),
    mode: "entra",
  };
}
